import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { CheckCircle, XCircle } from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface StripeResultDialogProps {
    type: "success" | "cancel";
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    redirectPath: string;
    titleKey: string;
    descriptionKey: string;
    buttonKey: string;
}

const StripeResultDialog = ({
    type,
    isOpen,
    onOpenChange,
    redirectPath,
    titleKey,
    descriptionKey,
    buttonKey,
}: StripeResultDialogProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleClose = () => {
        onOpenChange(false);
        navigate(redirectPath, { replace: true });
    };

    const getIcon = (): ReactNode => {
        if (type === "success") {
            return (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success-base/10">
                    <CheckCircle className="h-8 w-8 text-success-base" />
                </div>
            );
        }

        return (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <XCircle className="h-8 w-8 text-destructive" />
            </div>
        );
    };

    return (
        <div className="flex h-screen w-screen items-center justify-center bg-background">
            <Dialog open={isOpen} modal>
                <DialogContent
                    className="w-min justify-center gap-5 px-20 [&>button]:hidden"
                    onEscapeKeyDown={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}
                    onPointerDownOutside={(e) => e.preventDefault()}
                >
                    <DialogHeader className="items-center gap-2">
                        {getIcon()}
                        <DialogTitle className="text-center text-xl">
                            {t(titleKey)}
                        </DialogTitle>
                        <DialogDescription className="text-center">
                            {t(descriptionKey)}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="sm:justify-center">
                        <Button onClick={handleClose} variant="purpleGradient">
                            {t(buttonKey)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default StripeResultDialog;
